import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Loader2, CheckCircle2, ShieldCheck, Tag, Clock3, Ticket } from "lucide-react";
import { api, formatApiError, formatDate, money } from "../lib/api";
import { useContent } from "../lib/content";
import { TicketCard } from "./TicketCard";

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

export const BookingModal = ({ workshop, onClose, onBooked }) => {
  const { t } = useContent();
  const datesList = useMemo(() => (workshop.dates?.length > 1 ? workshop.dates : null), [workshop]);
  const [selectedDate, setSelectedDate] = useState(workshop.date);
  const [dateSlots, setDateSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    if (!datesList) return;
    let alive = true;
    setSlotsLoading(true);
    api.get(`/workshops/${workshop._id}`, { params: { date: selectedDate } })
      .then(({ data }) => { if (alive) setDateSlots(data.timeslots); })
      .catch(() => {})
      .finally(() => { if (alive) setSlotsLoading(false); });
    return () => { alive = false; };
  }, [datesList, selectedDate, workshop._id]);

  const slots = useMemo(
    () => (datesList ? dateSlots || [] : workshop.timeslots || []),
    [datesList, dateSlots, workshop]
  );
  const firstOpen = slots.find((s) => s.capacity - s.booked > 0) || slots[0];

  const [step, setStep] = useState("details");
  const [slotId, setSlotId] = useState(firstOpen?.id || "");
  const [seats, setSeats] = useState(1);
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", notes: "" });
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const methods = useRef({});
  const released = useRef(false);
  const bookingRef = useRef(null);

  const slot = slots.find((s) => s.id === slotId) || firstOpen;
  const seatsLeft = slot ? Math.max(0, slot.capacity - slot.booked) : 0;
  const tiers = useMemo(() => workshop.ticket_tiers || [], [workshop]);
  const [tierId, setTierId] = useState(tiers[0]?.id || "");
  const tier = tiers.find((tr) => tr.id === tierId) || tiers[0] || null;
  const unitPrice = tier ? tier.price_cents : workshop.price_cents;
  const subtotal = unitPrice * seats;
  const estDiscount = promo ? Math.min(promo.discount_cents, subtotal > 100 ? subtotal - 100 : 0) : 0;
  const estTotal = subtotal - estDiscount;

  useEffect(() => {
    setSeats((s) => Math.min(s, Math.max(1, Math.min(12, seatsLeft))));
  }, [slotId, seatsLeft]);

  const releasePending = useCallback(() => {
    if (bookingRef.current && !confirmation && !released.current) {
      released.current = true;
      api.delete(`/bookings/${bookingRef.current.booking_id}`).catch(() => {});
    }
  }, [confirmation]);

  const handleClose = useCallback(() => {
    releasePending();
    onClose();
  }, [releasePending, onClose]);

  useEffect(() => {
    return () => releasePending();
  }, [releasePending]);

  useEffect(() => {
    if (step !== "payment" || !booking || !window.Square) return;
    let alive = true;
    let card = null;
    (async () => {
      try {
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        card = await payments.card();
        await card.attach("#mas-card-container");
        methods.current.card = card;

        const totalStr = (booking.total_cents / 100).toFixed(2);
        const req = payments.paymentRequest({
          countryCode: "US",
          currencyCode: "USD",
          total: { amount: totalStr, label: `${workshop.title} at MAS Arts` },
        });
        try {
          const applePay = await payments.applePay(req);
          if (applePay && alive) {
            methods.current.applePay = applePay;
            document.querySelector("#mas-apple-pay")?.classList.remove("hidden");
          }
        } catch { /* wallet unavailable on this device */ }
        try {
          const cashApp = await payments.cashAppPay(req, {
            redirectURL: window.location.href,
            referenceId: booking.booking_code,
          });
          if (alive) {
            await cashApp.attach("#mas-cash-app-container");
            cashApp.addEventListener("ontokenization", (e) => {
              const { tokenResult, error: tErr } = e.detail;
              if (tErr) return setError("Cash App authorization failed");
              if (tokenResult.status === "OK") submitPayment(tokenResult.token);
            });
            methods.current.cashApp = cashApp;
          }
        } catch { /* wallet unavailable */ }
        try {
          const afterpay = await payments.afterpayClearpay(req);
          if (alive) {
            await afterpay.attach("#mas-afterpay-container");
            methods.current.afterpay = afterpay;
          }
        } catch { /* method unavailable for this seller/amount */ }
      } catch (e) {
        if (alive) setError(e.message || "Payment form failed to load");
      }
    })();
    return () => {
      alive = false;
      try { card?.destroy(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, booking]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoError("");
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promos/validate", {
        code: promoInput.trim(),
        amount_cents: subtotal,
      });
      if (!data.valid) {
        setPromo(null);
        setPromoError(data.message || "That code isn't valid.");
      } else {
        setPromo(data);
      }
    } catch {
      setPromoError("Couldn't check that code right now.");
    } finally {
      setPromoBusy(false);
    }
  };

  const createBooking = async () => {
    setError("");
    if (!slot) return setError("Please pick a timeslot.");
    if (form.guest_name.trim().length < 2) return setError("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(form.guest_email)) return setError("Please enter a valid email.");
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        workshop_id: workshop._id,
        timeslot_id: slot.id,
        date: datesList ? selectedDate : undefined,
        tier_id: tier ? tier.id : undefined,
        seats,
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        notes: form.notes,
        promo_code: promo ? promo.code : undefined,
        terms_accepted: terms,
      });
      setBooking(data);
      bookingRef.current = data;
      setStep("payment");
    } catch (e) {
      setError(formatApiError(e, "Could not reserve those seats."));
    } finally {
      setBusy(false);
    }
  };

  const submitPayment = async (sourceId, verificationToken) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/payments", {
        booking_id: booking.booking_id,
        source_id: sourceId,
        verification_token: verificationToken || undefined,
      });
      released.current = true;
      setConfirmation(data);
      setStep("confirmed");
      onBooked?.(data);
    } catch (e) {
      setError(formatApiError(e, "Payment failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const payByCard = async () => {
    if (!methods.current.card) return setError("Payment form is still loading.");
    const result = await methods.current.card.tokenize();
    if (result.status !== "OK") return setError("Please check your card details.");
    let verificationToken;
    try {
      const v = await window.Square
        .payments(SQUARE_APP_ID, SQUARE_LOCATION_ID)
        .verifyBuyer(result.token, {
          amount: (booking.total_cents / 100).toFixed(2),
          currencyCode: "USD",
          intent: "CHARGE",
          billingContact: { familyName: form.guest_name, email: form.guest_email },
        });
      verificationToken = v?.token;
    } catch { /* verification optional */ }
    await submitPayment(result.token, verificationToken);
  };

  const payAfterpay = async () => {
    if (!methods.current.afterpay) return;
    const result = await methods.current.afterpay.tokenize();
    if (result.status !== "OK") return setError("Afterpay was declined.");
    await submitPayment(result.token);
  };

  const payApplePay = async () => {
    if (!methods.current.applePay) return;
    const result = await methods.current.applePay.tokenize();
    if (result.status !== "OK") return setError("Apple Pay failed.");
    await submitPayment(result.token);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <motion.div
      data-testid="booking-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/45 backdrop-blur-sm p-0 sm:p-6"
      onClick={handleClose}
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg max-h-[92svh] overflow-y-auto rounded-t-[28px] sm:rounded-[28px] bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.5)]"
      >
        <div className="relative h-40">
          <img src={workshop.image} alt={workshop.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <button
            data-testid="booking-close-button"
            onClick={handleClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-[#1c1a17] hover:text-white transition-colors shadow"
            aria-label="Close booking"
          >
            <X size={16} />
          </button>
          <div className="absolute bottom-4 left-6 right-6 text-white">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/70">{workshop.theme}</p>
            <h3 className="font-display font-extrabold text-2xl tracking-tight mt-0.5">{workshop.title}</h3>
            <p className="text-sm text-white/75 mt-0.5 font-medium">
              {formatDate(selectedDate)}{datesList ? ` · ${datesList.length} dates` : ""} · {tiers.length > 0
                ? `from ${money(Math.min(...tiers.map((tr) => tr.price_cents)))} / seat`
                : `${money(workshop.price_cents)} / seat`}
            </p>
          </div>
        </div>

        <div className="p-6">
          {step === "details" && (
            <div className="space-y-5">
              {datesList && (
                <div>
                  <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400 flex items-center gap-1.5">
                    <Clock3 size={12} /> Pick your date
                  </label>
                  <div className="mt-2.5 flex gap-2 overflow-x-auto pb-1">
                    {(workshop.dates_summary || datesList.map((d) => ({ date: d, seats_left: null }))).map((d) => {
                      const active = selectedDate === d.date;
                      const full = d.seats_left === 0;
                      return (
                        <button
                          key={d.date}
                          data-testid={`booking-date-${d.date}`}
                          onClick={() => { if (!full) { setSelectedDate(d.date); setSlotId(""); } }}
                          disabled={full}
                          className={`rounded-2xl border px-3.5 py-2.5 text-center shrink-0 transition-all duration-300 ${
                            full
                              ? "border-neutral-100 text-neutral-300 cursor-not-allowed"
                              : active
                                ? "border-[#1c1a17] bg-[#1c1a17]/[0.06] shadow-[0_8px_20px_-10px_rgba(28,26,23,0.5)]"
                                : "border-neutral-200 hover:border-neutral-400"
                          }`}
                        >
                          <span className={`block text-xs font-extrabold whitespace-nowrap ${active ? "text-[#1c1a17]" : ""}`}>
                            {formatDate(d.date)}
                          </span>
                          <span className="block text-[10px] font-semibold text-neutral-400 mt-0.5">
                            {d.seats_left === null ? "" : full ? "Full" : `${d.seats_left} left`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400 flex items-center gap-1.5">
                  <Clock3 size={12} /> Pick your timeslot
                  {slotsLoading && <Loader2 size={12} className="animate-spin" />}
                </label>
                <div className="mt-2.5 grid grid-cols-3 gap-2">
                  {slots.map((s) => {
                    const left = Math.max(0, s.capacity - s.booked);
                    const active = slotId === s.id;
                    return (
                      <button
                        key={s.id}
                        data-testid={`booking-timeslot-${s.id}`}
                        onClick={() => left > 0 && setSlotId(s.id)}
                        disabled={left === 0}
                        className={`rounded-2xl border px-2 py-3 text-center transition-all duration-300 ${
                          left === 0
                            ? "border-neutral-100 text-neutral-300 cursor-not-allowed"
                            : active
                              ? "border-[#1c1a17] bg-[#1c1a17]/[0.06] shadow-[0_8px_20px_-10px_rgba(28,26,23,0.5)]"
                              : "border-neutral-200 hover:border-neutral-400"
                        }`}
                      >
                        <span className={`block text-sm font-extrabold ${active ? "text-[#1c1a17]" : ""}`}>{s.label}</span>
                        <span className="block text-[10px] font-semibold text-neutral-400 mt-0.5">
                          {left === 0 ? "Full" : `${left} left`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {tiers.length > 0 && (
                <div>
                  <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400 flex items-center gap-1.5">
                    <Ticket size={12} /> Pick your ticket type
                  </label>
                  <div className="mt-2.5 grid gap-2">
                    {tiers.map((tr) => {
                      const active = (tier?.id || "") === tr.id;
                      return (
                        <button
                          key={tr.id}
                          data-testid={`booking-tier-${tr.id}`}
                          onClick={() => setTierId(tr.id)}
                          className={`rounded-2xl border px-4 py-3.5 flex items-center justify-between gap-3 text-left transition-all duration-300 ${
                            active
                              ? "border-[#1c1a17] bg-[#1c1a17]/[0.06] shadow-[0_8px_20px_-10px_rgba(28,26,23,0.5)]"
                              : "border-neutral-200 hover:border-neutral-400"
                          }`}
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${active ? "border-[#c63f3e]" : "border-neutral-300"}`}>
                              {active && <span className="w-2 h-2 rounded-full bg-[#c63f3e]" />}
                            </span>
                            <span className={`text-sm font-extrabold truncate ${active ? "text-[#1c1a17]" : "text-neutral-600"}`}>
                              {tr.name}
                            </span>
                          </span>
                          <span className="font-display font-extrabold text-base shrink-0">{money(tr.price_cents)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400">Tickets</label>
                <div className="mt-2 flex items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50/60 p-3">
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="booking-seats-minus"
                      onClick={() => setSeats((s) => Math.max(1, s - 1))}
                      className="w-9 h-9 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-black transition-colors"
                    >
                      <Minus size={15} />
                    </button>
                    <span data-testid="booking-seats-input" className="font-display font-extrabold text-xl w-6 text-center">{seats}</span>
                    <button
                      data-testid="booking-seats-plus"
                      onClick={() => setSeats((s) => Math.min(Math.min(12, seatsLeft), s + 1))}
                      className="w-9 h-9 rounded-full border border-neutral-200 bg-white flex items-center justify-center hover:border-black transition-colors"
                    >
                      <Plus size={15} />
                    </button>
                  </div>
                  <span className="text-sm font-semibold text-neutral-400">{seatsLeft} left in this slot</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400">Name</label>
                  <input
                    data-testid="booking-name-input"
                    value={form.guest_name}
                    onChange={set("guest_name")}
                    placeholder="Your name"
                    className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1c1a17] placeholder:text-neutral-300"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400">Phone</label>
                  <input
                    data-testid="booking-phone-input"
                    value={form.guest_phone}
                    onChange={set("guest_phone")}
                    placeholder="(920) 555-0100"
                    className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1c1a17] placeholder:text-neutral-300"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400">Email</label>
                <input
                  data-testid="booking-email-input"
                  type="email"
                  value={form.guest_email}
                  onChange={set("guest_email")}
                  placeholder="you@email.com"
                  className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1c1a17] placeholder:text-neutral-300"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400">Notes (optional)</label>
                <textarea
                  data-testid="booking-notes-input"
                  value={form.notes}
                  onChange={set("notes")}
                  rows={2}
                  placeholder="Occasion, accessibility needs, group names…"
                  className="mt-2 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#1c1a17] placeholder:text-neutral-300 resize-none"
                />
              </div>

              <div>
                <label className="text-[11px] font-bold tracking-[0.14em] uppercase text-neutral-400 flex items-center gap-1.5">
                  <Tag size={12} /> Promo code (optional)
                </label>
                <div className="mt-2 flex gap-2">
                  <input
                    data-testid="booking-promo-input"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                    placeholder="e.g. FIRSTPAINT"
                    className="flex-1 rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm font-mono-mas tracking-wider outline-none focus:border-[#1c1a17] placeholder:text-neutral-300 uppercase"
                  />
                  <button
                    data-testid="booking-promo-apply"
                    onClick={applyPromo}
                    disabled={promoBusy || !promoInput.trim()}
                    className="rounded-2xl border border-neutral-200 px-5 text-sm font-bold hover:border-black transition-colors disabled:opacity-40"
                  >
                    {promoBusy ? <Loader2 size={15} className="animate-spin" /> : "Apply"}
                  </button>
                </div>
                {promo && (
                  <p data-testid="booking-promo-success" className="mt-2 text-xs font-bold text-emerald-600 flex items-center gap-1.5">
                    <CheckCircle2 size={13} /> {promo.code} applied — {promo.label}
                  </p>
                )}
                {promoError && (
                  <p data-testid="booking-promo-error" className="mt-2 text-xs font-semibold text-red-500">{promoError}</p>
                )}
              </div>

              <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-4 space-y-1.5 text-sm">
                <div className="flex justify-between text-neutral-500">
                  <span>{seats} × {tier ? tier.name : "seat"}{seats > 1 ? "s" : ""} · {money(unitPrice)} each</span>
                  <span className="font-semibold">{money(subtotal)}</span>
                </div>
                {estDiscount > 0 && (
                  <div className="flex justify-between text-emerald-600 font-semibold">
                    <span>Promo {promo?.code}</span>
                    <span>-{money(estDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-display font-extrabold text-lg pt-1.5 border-t border-neutral-200">
                  <span>Total</span>
                  <span data-testid="booking-total">{money(estTotal)}</span>
                </div>
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-4">
                <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-neutral-400 mb-2">
                  House Policies &amp; Terms
                </p>
                <div
                  data-testid="booking-terms-scrollbox"
                  className="max-h-36 overflow-y-auto rounded-xl border border-neutral-200 bg-white px-4 py-3 text-[11px] leading-relaxed text-neutral-500 font-medium space-y-3"
                >
                  {[1, 2, 3, 4].map((n) => (
                    <p key={n}>
                      <span className="font-bold text-[#1d1d1b]">{t(`policy_${n}_title`)}: </span>
                      {t(`policy_${n}_text`)}
                    </p>
                  ))}
                  <p className="whitespace-pre-line">{t("terms_text")}</p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    data-testid="booking-terms-checkbox"
                    checked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-[#1d1d1b] shrink-0"
                  />
                  <span className="text-xs text-neutral-500 font-medium leading-relaxed">
                    I have read and agree to the House Policies &amp; Terms above, including that{" "}
                    <span className="font-bold text-[#1d1d1b]">all sales are final — no refunds</span>.
                  </span>
                </label>
              </div>

              <AnimatePresence>{error && <ErrorLine text={error} />}</AnimatePresence>

              <button
                data-testid="booking-continue-button"
                onClick={createBooking}
                disabled={busy || !slot || !terms}
                className="btn-primary w-full rounded-full disabled:opacity-50 disabled:cursor-not-allowed py-4 font-bold flex items-center justify-center gap-2 text-[15px]"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                {terms ? `Continue to Payment · ${money(estTotal)}` : "Accept the policies to continue"}
              </button>
              <p className="text-center text-[11px] text-neutral-400 font-medium">
                Seats are held while you pay. All supplies included.
              </p>
            </div>
          )}

          {step === "payment" && booking && (
            <div className="space-y-5">
              <div className="flex items-center justify-between rounded-2xl border border-neutral-200 bg-neutral-50/60 p-4">
                <div>
                  <p className="text-sm font-medium text-neutral-500">
                    {seats} seat{seats > 1 ? "s" : ""} · {booking.timeslot_label} · {booking.booking_code}
                  </p>
                  <p className="font-display font-extrabold text-2xl tracking-tight">{money(booking.total_cents)}</p>
                  {booking.discount_cents > 0 && (
                    <p className="text-xs font-bold text-emerald-600 mt-0.5">Promo saved you {money(booking.discount_cents)}</p>
                  )}
                </div>
                <ShieldCheck size={28} className="text-[#1c1a17]" />
              </div>

              <div data-testid="booking-square-payment-form" className="space-y-4">
                <div id="mas-card-container" className="min-h-[90px] rounded-2xl border border-neutral-200 overflow-hidden" />
                <button
                  data-testid="booking-submit-button"
                  onClick={payByCard}
                  disabled={busy}
                  className="btn-primary w-full rounded-full disabled:opacity-60 py-4 font-bold flex items-center justify-center gap-2 text-[15px]"
                >
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Pay {money(booking.total_cents)} by Card
                </button>

                <div className="flex items-center gap-3 text-[11px] font-bold tracking-[0.16em] uppercase text-neutral-300">
                  <span className="h-px flex-1 bg-neutral-200" /> or <span className="h-px flex-1 bg-neutral-200" />
                </div>

                <button
                  id="mas-apple-pay"
                  data-testid="booking-apple-pay-button"
                  onClick={payApplePay}
                  className="hidden w-full rounded-full bg-black text-white py-3.5 font-bold text-sm hover:bg-neutral-800 transition-colors"
                >
                  Pay with Apple Pay
                </button>
                <div id="mas-cash-app-container" data-testid="booking-cash-app-button" className="min-h-[44px]" />
                <div
                  id="mas-afterpay-container"
                  data-testid="booking-afterpay-button"
                  onClick={busy ? undefined : payAfterpay}
                  className="min-h-[44px] cursor-pointer"
                />
              </div>

              <AnimatePresence>{error && <ErrorLine text={error} />}</AnimatePresence>
              <p className="text-center text-[11px] text-neutral-400 font-medium">
                Secure checkout by Square. Card details never touch our servers.
              </p>
            </div>
          )}

          {step === "confirmed" && confirmation && (
            <motion.div
              data-testid="booking-confirmation-screen"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-4 space-y-5 text-left"
            >
              <div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                  className="w-14 h-14 rounded-full bg-[#eac119] flex items-center justify-center shadow-[0_18px_36px_-14px_rgba(234,193,25,0.7)]"
                >
                  <CheckCircle2 size={28} className="text-[#1d1d1b]" />
                </motion.div>
                <h4 className="font-display font-extrabold text-3xl tracking-tight mt-4">
                  You're Booked,{" "}
                  <span className="font-editorial italic font-medium text-[#c63f3e]">
                    {(form.guest_name || "friend").split(" ")[0]}!
                  </span>
                </h4>
                <p className="text-sm text-neutral-500 font-medium mt-2">
                  A receipt with your booking code is on its way to{" "}
                  <span className="font-bold text-[#1d1d1b]">{form.guest_email || "your inbox"}</span>.
                  Show the code when you arrive.
                </p>
              </div>

              <TicketCard
                booking={{
                  booking_code: confirmation.booking_code,
                  workshop_title: workshop.title,
                  workshop_date: confirmation.workshop_date || selectedDate,
                  timeslot_label: booking?.timeslot_label || "",
                  tier_name: booking?.tier_name || tier?.name || "",
                  seats,
                  image: workshop.image,
                  total_cents: booking?.total_cents || 0,
                }}
              />

              <a
                data-testid="booking-ticket-page-link"
                href={`/ticket/${confirmation.booking_code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-xs font-bold text-[#1d1d1b] underline underline-offset-4 hover:text-black"
              >
                Save your ticket page — it lives at /ticket/{confirmation.booking_code}
              </a>

              <button
                data-testid="booking-done-button"
                onClick={handleClose}
                className="w-full rounded-full border border-neutral-200 py-3.5 text-sm font-bold hover:border-black transition-colors"
              >
                Done
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const ErrorLine = ({ text }) => (
  <motion.p
    data-testid="booking-error"
    initial={{ opacity: 0, y: -4 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-2xl px-4 py-2.5"
  >
    {text}
  </motion.p>
);
